import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, Heart, MapPin, CreditCard, LogOut, ChevronRight, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useSite } from '../../context/SiteContext';
import './Account.css';

const Account = () => {
  const { user, signOut, isAuthenticated } = useAuth();
  const { cartItems } = useCart();
  const { orderPrefix } = useSite();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, navigate]);

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const menuItems = [
    { icon: Package, label: 'My Orders', description: 'Track and view your order history', link: '/orders' },
    { icon: Heart, label: 'Wishlist', description: 'View your saved items', link: '/wishlist' },
    { icon: MapPin, label: 'Addresses', description: 'Manage your shipping addresses', link: '/addresses' },
    { icon: CreditCard, label: 'Payment Methods', description: 'Manage your payment options', link: '/payment-methods' },
  ];

  // Mock order history
  const recentOrders = [
    { id: `${orderPrefix}12345678`, date: '2024-01-15', status: 'Delivered', total: 149.99, items: 3 },
    { id: `${orderPrefix}87654321`, date: '2024-01-10', status: 'Shipped', total: 89.50, items: 2 },
  ];

  if (!user) return null;

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Welcome Banner */}
        <div className="account-welcome">
          <div className="account-welcome-content">
            <h1>Welcome back, {user.name}!</h1>
            <p>Manage your account, track orders, and enjoy exclusive member benefits.</p>
          </div>
          <Link to="/shop" className="account-welcome-btn">
            <ShoppingBag size={18} />
            Start Shopping
          </Link>
        </div>

        {/* Quick Stats */}
        <div className="account-stats">
          <div className="account-stat">
            <span className="account-stat-value">{cartItems.length}</span>
            <span className="account-stat-label">Items in Bag</span>
          </div>
          <div className="account-stat">
            <span className="account-stat-value">0</span>
            <span className="account-stat-label">Wishlist Items</span>
          </div>
          <div className="account-stat">
            <span className="account-stat-value">2</span>
            <span className="account-stat-label">Total Orders</span>
          </div>
        </div>

        <div className="account-content">
          {/* Menu Items */}
          <div className="account-menu">
            <h2>My Account</h2>
            {menuItems.map((item) => (
              <Link key={item.label} to={item.link} className="account-menu-item">
                <div className="account-menu-icon">
                  <item.icon size={24} />
                </div>
                <div className="account-menu-content">
                  <h3>{item.label}</h3>
                  <p>{item.description}</p>
                </div>
                <ChevronRight size={20} className="account-menu-arrow" />
              </Link>
            ))}
          </div>

          {/* Recent Orders */}
          <div className="account-orders">
            <div className="account-orders-header">
              <h2>Recent Orders</h2>
              <Link to="/orders">View All</Link>
            </div>

            {recentOrders.length > 0 ? (
              <div className="account-orders-list">
                {recentOrders.map((order) => (
                  <div key={order.id} className="account-order-item">
                    <div className="account-order-info">
                      <span className="account-order-id">Order #{order.id}</span>
                      <span className="account-order-date">{order.date}</span>
                    </div>
                    <div className="account-order-details">
                      <span className="account-order-items">{order.items} items</span>
                      <span className="account-order-total">${order.total.toFixed(2)}</span>
                    </div>
                    <span className={`account-order-status ${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="account-orders-empty">
                <Package size={48} />
                <p>No orders yet</p>
                <Link to="/shop">Start Shopping</Link>
              </div>
            )}
          </div>
        </div>

        {/* Account Info */}
        <div className="account-info-section">
          <h2>Account Information</h2>
          <div className="account-info-grid">
            <div className="account-info-card">
              <h3>Contact Information</h3>
              <p>{user.name}</p>
              <p>{user.email}</p>
              <button className="account-info-edit">Edit</button>
            </div>
            <div className="account-info-card">
              <h3>Default Shipping Address</h3>
              <p>No default address saved</p>
              <button className="account-info-edit">Add Address</button>
            </div>
          </div>
        </div>

        {/* Sign Out */}
        <div className="account-signout">
          <button onClick={handleSignOut} className="account-signout-btn">
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;
